import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isPaidPlan, planDisplayName } from '../../utils/plans';

export default function Settings() {
  const { root } = useSelector(state => state.api);
  const user = useSelector(state => state.user);

  const [loading, setLoading] = React.useState(false);
  const [description, setDescription] = React.useState(null);
  const [accountName, setAccountName] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    dispatch({ type: 'SET_USER', method: 'get', path: '/auth/user' });
  }, [dispatch]);

  const displayUsername = (accountName ?? user?.username ?? '').toLowerCase();
  const tokensRemaining = Number.isFinite(user?.aiTokensRemaining) ? user.aiTokensRemaining : null;
  const tokensLimit = Number.isFinite(user?.aiTokensLimit) ? user.aiTokensLimit : null;
  const tokensUsed = Number.isFinite(user?.aiTokensUsed) ? user.aiTokensUsed : null;
  const tokensUnlimited = !!user?.aiTokensUnlimited;
  const tokensLoaded = tokensUnlimited || (tokensRemaining != null && tokensLimit != null);
  const tokenUsagePct =
    tokensLimit > 0 && Number.isFinite(tokensUsed)
      ? Math.min(100, Math.round((tokensUsed / tokensLimit) * 100))
      : 0;
  const tokenResetMode = user?.aiTokensResetMode || 'calendar';
  const tokenResetsAt = user?.aiTokensResetsAt || null;
  const tokenResetLabel = (() => {
    if (tokenResetMode === 'billing') {
      if (tokenResetsAt) {
        try {
          const formatted = new Date(tokenResetsAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          return `Resets when your subscription renews · ${formatted}`;
        } catch {
          return 'Resets when your subscription renews';
        }
      }
      return 'Resets when your subscription renews';
    }
    return 'Resets on the 1st of each month';
  })();

  const onChangeHandler = event => {
    const file = event.target.files?.[0];
    if (file) handleFileSubmit(file);
    event.target.value = '';
  };

  const handleNameSubmit = () => {
    if (!accountName) return;
    const re = /^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/g;

    if (!re.test(accountName)) {
      setMessage('NOT_MATCH');
      return;
    }
    setMessage(null);
    setLoading(true);
    fetch(`${root}/auth/name`, {
      method: 'post',
      body: JSON.stringify({ text: accountName }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(res => res.json())
      .then(json => {
        setLoading(false);
        setMessage(json.type);
        if (json.type === 'DONE') {
          dispatch({ type: 'CHANGE_USER_NAME_$', text: accountName.toLowerCase() });
          navigate(`/${accountName.toLowerCase()}`);
        }
      })
      .catch(err => console.log(err));
  };

  const handleDescriptionSubmit = () => {
    if (!description) return;
    setMessage(null);
    setLoading(true);
    fetch(`${root}/auth/description`, {
      method: 'post',
      body: JSON.stringify({ text: description }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(res => res.json())
      .then(json => {
        setMessage(json.type);
        setLoading(false);
        if (json.type === 'DONE') dispatch({ type: 'CHANGE_USER_DESCRIPTION_$', text: description });
      })
      .catch(err => console.log(err));
  };

  const handleFileSubmit = selectedFile => {
    setLoading(true);
    const data = new FormData();
    data.append('image', selectedFile, selectedFile.name);
    fetch(`${root}/auth/picture`, {
      method: 'post',
      body: data,
      credentials: 'include',
    })
      .then(res => res.json())
      .then(json => {
        setLoading(false);
        dispatch({ type: 'CHANGE_USER_PICTURE_$', imgUrl: json.imgUrl });
      })
      .catch(err => console.log(err));
  };

  function handleSignOut() {
    navigate('/en');
    fetch(`${root}/auth/signout`, { credentials: 'include' })
      .then(res => res.json())
      .then(() => dispatch({ type: 'DESTROY_USER_$' }))
      .catch(err => console.log(err));
  }

  const nameErrorMessage =
    message === 'NOT_MATCH'
      ? 'Sorry, you cannot use this username. For now, Hashely follows the Instagram username policy.'
      : message === 'ALREADY_EXSIST'
        ? 'This username is already taken.'
        : null;

  if (!user) return null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pt: { xs: 10, sm: 11 },
        pb: { xs: 3, sm: 5 },
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your profile and account
            </Typography>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <IconButton
                    aria-label="View your profile"
                    onClick={() => {
                      if (!user?.username) return;
                      navigate(`/${user.username}`);
                    }}
                    sx={{ p: 0 }}
                  >
                    <Avatar
                      src={user.profileImageUrl}
                      sx={{
                        width: 72,
                        height: 72,
                        fontSize: 28,
                        bgcolor: 'primary.main',
                      }}
                    />
                  </IconButton>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Profile photo
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={onChangeHandler}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCameraOutlinedIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none' }}
                    >
                      Upload photo
                    </Button>
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Username
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="Username"
                      defaultValue={user.completed ? user.username : ''}
                      onChange={e => setAccountName(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                              @
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        displayUsername
                          ? `Your username is @${displayUsername} — friends tag you with @${displayUsername} to talk to you.`
                          : 'Choose a username — friends tag you with @username to talk to you.'
                      }
                      FormHelperTextProps={{ sx: { mx: 0, mt: 1, lineHeight: 1.5 } }}
                    />
                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<SaveRoundedIcon />}
                      onClick={handleNameSubmit}
                      sx={{ minWidth: { sm: 100 }, borderRadius: 2, textTransform: 'none', px: 2.5 }}
                    >
                      Save
                    </Button>
                  </Stack>
                  {!user.completed && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Choose your social @name to finish setting up your profile.
                    </Alert>
                  )}
                  {nameErrorMessage && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {nameErrorMessage}
                    </Alert>
                  )}
                  {message === 'NAME_DONE' && (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      Username saved.
                    </Alert>
                  )}
                </Stack>

                {user.completed && (
                  <>
                    <Divider />
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                        <TextField
                          fullWidth
                          label="Description"
                          multiline
                          minRows={2}
                          defaultValue={user.description}
                          placeholder="Tell people a little about yourself"
                          onChange={e => setDescription(e.target.value)}
                        />
                        <Button
                          variant="contained"
                          disableElevation
                          startIcon={<SaveRoundedIcon />}
                          onClick={handleDescriptionSubmit}
                          sx={{ minWidth: { sm: 100 }, borderRadius: 2, textTransform: 'none', px: 2.5 }}
                        >
                          Save
                        </Button>
                      </Stack>
                      {message === 'DONE' && (
                        <Alert severity="success" sx={{ borderRadius: 2 }}>
                          Description saved.
                        </Alert>
                      )}
                    </Stack>
                  </>
                )}

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current plan
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                      {planDisplayName(user.subscriptionStatus)}
                    </Typography>
                    <Chip
                      size="small"
                      label={isPaidPlan(user.subscriptionStatus) ? 'Active' : 'Free'}
                      sx={{
                        fontWeight: 600,
                        bgcolor: isPaidPlan(user.subscriptionStatus) ? '#fef3c7' : 'grey.100',
                        color: isPaidPlan(user.subscriptionStatus) ? '#92400e' : 'text.secondary',
                      }}
                    />
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/pricing')}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, borderRadius: 2, textTransform: 'none' }}
                >
                  {isPaidPlan(user.subscriptionStatus) ? 'Manage plan' : 'Upgrade'}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    AI allowance this period
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {tokenResetLabel}
                  </Typography>
                </Box>
                {!tokensLoaded ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading token balance…
                  </Typography>
                ) : tokensUnlimited ? (
                  <>
                    <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.03em' }}>
                      Unlimited
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      No monthly allowance cap
                    </Typography>
                  </>
                ) : (
                  <>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.03em' }}>
                        {tokensRemaining.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        of {tokensLimit.toLocaleString()} remaining
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={tokenUsagePct}
                      sx={{ height: 8, borderRadius: 999, bgcolor: 'grey.200' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {(tokensUsed ?? 0).toLocaleString()} used this period
                    </Typography>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<LogoutRoundedIcon />}
            onClick={handleSignOut}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.25,
              bgcolor: 'background.paper',
            }}
          >
            Sign out
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
